import { useEffect, useState } from 'react';
import history from '@history';
import { useDispatch } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import WdPageSimple from '@main/core/WdPageSimple';
import {
  BasicSelect,
  ButtonWithLoader,
  ComposedHeader,
  FormCardLayout,
  MetadataExpandForm,
} from '@main/components';
import { showMessage } from 'app/store/core/messageSlice';
import _ from 'lodash';
import { regexTitle } from 'src/app/utils/validations';
import { useRequest } from '@main/hooks';
import { useExtractSelector } from '@main/hooks/useExtractSelector';
import {
  catalogAccessTypes,
  catalogBuildings,
  catalogCompanies,
  catalogLocations,
  catalogZones,
  storeKiosk,
} from 'src/app/api/kiosk';
import { FormControlLabel, FormLabel, Grid, Switch } from '@mui/material';

const Root = styled(WdPageSimple)(({ theme }) => ({
  backgroundColor: '#FFF',
  '& .WdPageSimple-header': {},
  '& .WdPageSimple-content': {},
}));

const schema = yup.object().shape({
  name: yup
    .string()
    .matches(regexTitle, 'El nombre no es válido')
    .required('El nombre es obligatorio'),
  description: yup.string().min(6, 'La descripción debe de tener más de 6 caracteres'),
  accessType: yup.string().required('Selecciona un tipo de acceso'),
  company_key: yup.string().required('Selecciona una compañia'),
  location_key: yup.string().required('Selecciona una locación'),
  building_key: yup.string().required('Selecciona un edificio'),
  zone_key: yup.string().required('Selecciona una zona'),
  status: yup.boolean(),
});

const defaultValues = {
  // ---- Valores iniciales de formulario
  name: '',
  description: '',
  accessType: '',
  company_key: '',
  location_key: '',
  building_key: '',
  zone_key: '',
  status: true,
};

export const Create = () => {
  const dispatch = useDispatch();

  const [metadata, setMetadata] = useState({});
  const [metadataError, setMetadataError] = useState(false);

  // ----- Inicializacion de validadores de formulario
  const { control, formState, handleSubmit, watch } = useForm({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(schema),
  });
  const { isValid, dirtyFields, errors } = formState;

  // ------ Observadores -------------
  const watchCompanyKey = watch('company_key');
  const watchBuildingKey = watch('building_key');
  const watchLocationKey = watch('location_key');

  // ------ Request Store Registry ------
  const { makeRequest: tryStore, isLoading } = useRequest(storeKiosk);

  // ------ index Company & Location  ------
  const { dataSelect: companyDataSelect } = useExtractSelector(catalogCompanies, {
    emptyOption: false,
    label: 'label',
    value: 'value',
  });
  const { dataSelect: locationDataSelect, makeRequest: locationTryRequest } = useExtractSelector(
    catalogLocations,
    {
      emptyOption: false,
      label: 'label',
      value: 'value',
    }
  );
  const { dataSelect: buildingDataSelect, makeRequest: buildingTryRequest } = useExtractSelector(
    catalogBuildings,
    {
      autoFetch: false,
      emptyOption: false,
      label: 'label',
      value: 'value',
    }
  );
  const { dataSelect: zoneDataSelect, makeRequest: zoneTryRequest } = useExtractSelector(
    catalogZones,
    {
      autoFetch: false,
      emptyOption: false,
      label: 'label',
      value: 'value',
    }
  );

  const { dataSelect: accessTypeDataSelect } = useExtractSelector(catalogAccessTypes, {
    emptyOptions: true,
    label: 'label',
    value: 'value',
  });

  // ------------ Actualizacion anidada de Selects -------------
  useEffect(() => {
    if (watchCompanyKey === undefined || watchCompanyKey.trim() === '') return;
    locationTryRequest({
      company_key: watchCompanyKey,
    });
  }, [watchCompanyKey]);

  useEffect(() => {
    if (watchLocationKey === undefined || watchLocationKey.trim() === '') return;
    buildingTryRequest({
      location_key: watchLocationKey,
      company_key: watchCompanyKey,
    });
  }, [watchLocationKey, watchCompanyKey]);

  useEffect(() => {
    if (watchBuildingKey === undefined || watchBuildingKey.trim() === '') return;
    zoneTryRequest({
      building_key: watchBuildingKey,
      company_key: watchCompanyKey,
    });
  }, [watchBuildingKey, watchCompanyKey]);

  const handleUpdate = (form) => {
    const builtForm = {
      ...form,
    };
    console.log(JSON.stringify(builtForm, null, 3));
    tryStore(builtForm).then(({ isOk }) => {
      if (isOk) {
        dispatch(
          showMessage({
            message: 'Locación agregada correctamente',
            autoHideDuration: 4000,
            variant: 'success',
          })
        );
        history.replace('/kiosks');
      }
    });
  };

  return (
    <Root
      header={<ComposedHeader decorator="button" />}
      content={
        <Box
          className="flex justify-center items-start"
          sx={{
            width: '100%',
            height: '100%',
          }}
        >
          <FormCardLayout title="Agregar kiosko">
            <FormCardLayout.FullColum>
              <form
                name="locationsCreateForm"
                noValidate
                className="flex flex-col justify-center w-full"
                autoComplete="off"
              >
                <div className="flex flex-col">
                  <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="mb-32"
                        label="Nombre"
                        autoFocus
                        type="text"
                        error={!!errors.name}
                        helperText={errors?.name?.message}
                        variant="outlined"
                        required
                        fullWidth
                      />
                    )}
                  />
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="mb-32"
                        label="Descripción"
                        autoFocus
                        type="text"
                        error={!!errors.description}
                        helperText={errors?.description?.message}
                        variant="outlined"
                        fullWidth
                      />
                    )}
                  />
                  <Controller
                    name="accessType"
                    control={control}
                    render={({ field }) => (
                      <BasicSelect
                        value={field.value}
                        onChange={(value) => field.onChange(value)}
                        data={accessTypeDataSelect}
                        error={!!errors.company_key}
                        title="Tipo de acceso"
                        size="medium"
                        fullWidth
                        helperText={errors?.accessType?.message}
                        className="mb-32"
                      />
                    )}
                  />
                  <Grid container spacing={3} className="mb-32">
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="company_key"
                        control={control}
                        render={({ field }) => (
                          <BasicSelect
                            value={field.value}
                            onChange={(value) => field.onChange(value)}
                            data={companyDataSelect}
                            error={!!errors.company_key}
                            title="Compañía"
                            size="medium"
                            fullWidth
                            helperText={errors?.company_key?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="location_key"
                        control={control}
                        render={({ field }) => (
                          <BasicSelect
                            value={field.value}
                            onChange={(value) => field.onChange(value)}
                            data={locationDataSelect}
                            error={!!errors.location_key}
                            title="Locación"
                            size="medium"
                            helperText={errors?.location_key?.message}
                            fullWidth
                            disabled={
                              watchCompanyKey === undefined || watchCompanyKey.trim() === ''
                            }
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={3} className="mb-32">
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="building_key"
                        control={control}
                        render={({ field }) => (
                          <BasicSelect
                            value={field.value}
                            onChange={(value) => field.onChange(value)}
                            data={buildingDataSelect}
                            error={!!errors.building_key}
                            title="Edificio"
                            size="medium"
                            helperText={errors?.building_key?.message}
                            fullWidth
                            disabled={
                              watchLocationKey === undefined || watchLocationKey.trim() === ''
                            }
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="zone_key"
                        control={control}
                        render={({ field }) => (
                          <BasicSelect
                            value={field.value}
                            onChange={(value) => field.onChange(value)}
                            data={zoneDataSelect}
                            error={!!errors.zone_key}
                            title="Zona"
                            size="medium"
                            helperText={errors?.zone_key?.message}
                            fullWidth
                            disabled={
                              watchBuildingKey === undefined || watchBuildingKey.trim() === ''
                            }
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                  <Controller
                    name="status"
                    control={control}
                    className="mb-32 mt-32"
                    render={({ field }) => (
                      <div className="flex flex-col justify-center my-20 w-full items-center">
                        <FormLabel component="legend">Estatus</FormLabel>
                        <FormControlLabel
                          className="mb-32"
                          control={
                            <Switch
                              checked={field.value}
                              {...field}
                              inputProps={{ 'aria-label': 'Estatus' }}
                            />
                          }
                          label={field.value ? 'Activo' : 'Inactivo'}
                        />
                      </div>
                    )}
                  />
                </div>
              </form>
            </FormCardLayout.FullColum>
            <FormCardLayout.FullColum>
              <div>
                <MetadataExpandForm
                  onChange={(metafields) => setMetadata(metafields)}
                  onError={(metaError) => setMetadataError(metaError)}
                />
              </div>
            </FormCardLayout.FullColum>
            <FormCardLayout.RowButton>
              <Button
                onClick={() => history.replace('/kiosks')}
                variant="contained"
                className="w-full md:w-auto"
                color="error"
              >
                Cancelar
              </Button>
              <ButtonWithLoader
                onClick={handleSubmit(handleUpdate)}
                color="success"
                aria-label="Agregar locación"
                variant="contained"
                disabled={_.isEmpty(dirtyFields) || !isValid || metadataError}
                type="submit"
                isLoading={isLoading}
              >
                Agregar
              </ButtonWithLoader>
            </FormCardLayout.RowButton>
          </FormCardLayout>
        </Box>
      }
    />
  );
};
